import {
	ChangeDetectionStrategy,
	Component,
	computed,
	CUSTOM_ELEMENTS_SCHEMA,
	effect,
	forwardRef,
	inject,
	OnInit,
	ViewEncapsulation,
	WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { injectTranslation } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { isEqual, isUndefined } from '@skylab-shared/utils';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';
import { SelectValueAccessorDirective } from '../../../value-accessors/select-value-accessor.directive';
import { ManyFromListChip } from '../../filter-chips';
import { FILTER_CHIP_STRATEGY, FilterChipContext, FilterChipStrategy } from '../filter-chip-context';
import { FilterChipHostDirective } from '../filter-chip-host.directive';

@Component({
	selector: 'skl-many-from-list-filter-chip',
	templateUrl: './filter-chip-many-from-list.component.html',
	hostDirectives: [{ directive: FilterChipHostDirective, inputs: ['chip'] }],
	host: {
		'[class.has-value]': 'ctx.hasValue()'
	},
	providers: [
		FilterChipContext,
		{ provide: FILTER_CHIP_STRATEGY, useExisting: forwardRef(() => ManyFromListFilterChipComponent) }
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [TooltipDirective, SelectValueAccessorDirective, FormsModule, TranslatePipe],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManyFromListFilterChipComponent implements OnInit, FilterChipStrategy {
	readonly #host = inject(FilterChipHostDirective);
	protected readonly ctx = inject(FilterChipContext);
	protected readonly _T = injectTranslation();

	readonly chosenValues: unknown[] = [];

	protected readonly chip = computed(() => this.#host.chip() as ManyFromListChip);
	protected readonly value = this.ctx.value as WritableSignal<unknown[] | null>;
	protected readonly collection = this.ctx.collection;
	protected get converter() {
		return this.ctx.converter;
	}
	
	protected readonly visibleValue = computed(() => {
		const val = this.value();
		const collection = this.collection();
		if (!val || !Array.isArray(val)) return [];
		return val.filter(v => collection.some(item => item.value === v));
	});

	readonly showSelectedAllText = computed(() => {
		const chip = this.chip();
		const val = this.visibleValue();
		const limit = chip?.limit;
		return !!limit && (val?.length === this.collection().length);
	});

	readonly showSelectedCountText = computed(() => {
		const chip = this.chip();
		const val = this.visibleValue();
		const limit = chip?.limit;
		return limit !== undefined && limit < val?.length && (val?.length < this.collection().length);
	});

	readonly tooltipValues = computed(() => {
		const val = this.visibleValue();
		return val.map(elem => this.ctx.converter.convert(elem as string)?.label).filter(l => !!l);
	});

	constructor() {
		effect(() => {
			const chip = this.#host.chip();
			if (chip) this.ctx.chip.set(chip);
		});
	}

	public ngOnInit() {
		const chip = this.chip();
		this.ctx.chip.set(chip);

		this.ctx.buildCollection([...chip.collection || []]);
		this.setInitialValue();
		this.ctx.initConverter();
	}

	public setInitialValue() {
		const chip = this.chip();
		let val: unknown[] | null = Array.isArray(chip.value) 
			? [...chip.value].filter(v => !isUndefined(v)) 
			: (isUndefined(chip.value) || chip.value === null ? [] : [chip.value]);
		
		if (Array.isArray(val) && val.length === 0) {
			val = null;
		}

		this.value.set(val);
		this.ctx.setInitialChipValue(val);
	}

	public resetDefaultValue() {
		const chip = this.chip();
		const defaultValue = (!isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue) ?? null;
		const newValue = Array.isArray(defaultValue) ? [...defaultValue] : (defaultValue === null ? null : [defaultValue]);

		if (isEqual(newValue, this.value())) {
			return;
		}

		this.value.set(newValue);
		chip.value = newValue;
		chip.onChange(newValue);
	}

	public onValueChange(value: unknown) {
		const chip = this.chip();
		const collection = this.collection();
		let val: unknown[] | null = Array.isArray(value) 
			? value.filter(v => !isUndefined(v)) 
			: (isUndefined(value) || value === null ? [] : [value]);
		
		if (Array.isArray(val) && collection.length > 0) {
			val.sort((a, b) => {
				const indexA = collection.findIndex(item => item.value === a);
				const indexB = collection.findIndex(item => item.value === b);
				return indexA - indexB;
			});
		}

		if (val.length === 0) {
			val = null;
		}

		if (isEqual(val, this.value())) {
			return;
		}

		this.value.set(val);
		chip.value = val;
		chip.onChange(val);
	}

	public refresh() {
		const chip = this.chip();
		this.ctx.buildCollection([...chip.collection || []]);
		this.setInitialValue();
	}
}
